import { FC, useMemo, useCallback } from 'react'
import { SidebarScrollablePopupPlace } from 'components/features/pfm/SidebarPopupPlace/SidebarScrollablePopupPlace'
import { FeedfilterPeergroupPopupContainer } from 'components/features/socialFeed/FeedfilterPopup/FeedfilterPeergroupPopupContainer'
import { FeedFilterPostsPopup } from 'components/features/socialFeed/FeedfilterPopup/FeedFilterPostsPopup'
import { FeedFilterPeoplePopupContainer } from 'components/features/socialFeed/FeedfilterPopup/FeedFilterPeoplePopupContainer'
import { SidebarPopupPlace } from 'components/features/pfm/SidebarPopupPlace/SidebarPopupPlace'
import { FeedFilterTagsPopupContainer } from 'components/features/socialFeed/FeedfilterPopup/FeedFilterTagsPopupContainer'
import { FeedFilterYourPopupContainer } from 'components/features/socialFeed/FeedfilterPopup/FeedFilterYourPopupContainer'
import { useShallowSelector, useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { nationalAverageRelationIdSelector } from 'selectors/peergroupSelectors'
import { usePopupPlacePosition } from 'helpers/hookHelpers'
import { hidePopupFeedFilterActionCreator } from 'actions/feedActions'

type FeedfilterPopupPlaceProps = {}

export const FeedfilterPopupPlace: FC<FeedfilterPopupPlaceProps> = () => {
  const targetRect = useShallowSelector((state) => state.app.feed.filterPopup.rect)
  const shownFilter = useShallowSelector((state) => state.app.feed.filterPopup.shownFilter)
  const nationalAvarageRelationId = useStoreSelector(nationalAverageRelationIdSelector)

  const viewportSegment = useStoreSelector(viewportSegmentSelector)
  const position = usePopupPlacePosition()
  const dispatch = useStoreDispatch()
  const onHidePopup = useCallback(() => {
    dispatch(hidePopupFeedFilterActionCreator())
  }, [dispatch])

  const popupChild = useMemo(() => {
    if (!shownFilter) {
      return null
    }

    const onBackButtonClick = position === 'fill'
      ? onHidePopup
      : undefined

    const corners = position === 'fill' ? 'none' : 'right'

    if (shownFilter.type === 'peergroup') {
      return (
        <FeedfilterPeergroupPopupContainer
          isEveryone={ false }
          relationId={ shownFilter.relationId }
          corners={ corners }
          onBackButtonClick={ onBackButtonClick }
        />
      )
    }

    if (shownFilter.type === 'your-posts') {
      return (
        <FeedFilterYourPopupContainer
          corners={ corners }
          onBackButtonClick={ onBackButtonClick }
        />
      )
    }

    if (shownFilter.type === 'feedgroup' && shownFilter.slug === 'everyone' && typeof nationalAvarageRelationId === 'number') {
      return (
        <FeedfilterPeergroupPopupContainer
          isEveryone
          relationId={ nationalAvarageRelationId }
          corners={ corners }
          onBackButtonClick={ onBackButtonClick }
        />
      )
    }

    if (shownFilter.type === 'feedgroup' && shownFilter.slug === 'followed_posts') {
      return (
        <FeedFilterPostsPopup
          corners={ corners }
          onBackButtonClick={ onBackButtonClick }
        />
      )
    }

    if (shownFilter.type === 'feedgroup' && shownFilter.slug === 'followed_people') {
      return (
        <FeedFilterPeoplePopupContainer
          fill={ position === 'fill' }
          corners={ corners }
          onBackButtonClick={ onBackButtonClick }
        />
      )
    }

    if (shownFilter.type === 'feedgroup' && shownFilter.slug === 'followed_tags') {
      return (
        <FeedFilterTagsPopupContainer
          fill={ position === 'fill' }
          corners={ corners }
          onBackButtonClick={ onBackButtonClick }
        />
      )
    }

    return null
  }, [shownFilter, onHidePopup, position, nationalAvarageRelationId])

  const rootChild = useMemo(() => {
    if (!targetRect || !shownFilter || !popupChild) {
      return null
    }

    if (
      shownFilter.type === 'feedgroup' && (
        shownFilter.slug === 'followed_people'
        || shownFilter.slug === 'followed_tags'
      )
    ) {
      // without scroll
      return (
        <SidebarPopupPlace
          targetRect={ targetRect }
          position={ position }
        >
          { popupChild }
        </SidebarPopupPlace>
      )
    }

    return (
      <SidebarScrollablePopupPlace
        targetRect={ targetRect }
        position={ position }
        alwaysShowScrollbar={ viewportSegment !== 'desktop' }
      >
        { popupChild }
      </SidebarScrollablePopupPlace>
    )

  }, [targetRect, shownFilter, popupChild, position, viewportSegment])

  return rootChild
}
